import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  selectCurrentStudioScene,
  selectStudioImages,
  selectStudioVideos,
  StudioScene,
  StudioService,
  StudioImage,
  StudioVideo,
  StudioVideoShapeDescriptor,
} from '../../element-editor/store/studio';
import { cloneDeep } from 'lodash';
import { MediaType } from '../media-upload-modal/media-upload-modal.component';
import { StudioLayoutService } from '../../element-editor/store/studio/layout';
import { CanvasService } from '../../services/canvas/canvas.service';
import { StudioTextShapeDescriptor } from '../../element-editor/store/studio/text/studio-text-shape';

@Component({
  selector: 'nw-scene-input',
  templateUrl: './scene-input.component.html',
  styleUrls: ['./scene-input.component.scss'],
})
export class SceneInputComponent implements OnDestroy, AfterViewInit {
  currentScene: StudioScene | undefined;
  textValues: string[] = [];
  sceneImageIds: string[] = [];
  videoInstanceIds: string[] = [];
  allImages: Record<string, StudioImage> = {};
  allVideos: Record<string, StudioVideo> = {};
  showMediaUploadModal = false;
  mediaUploadType: MediaType = 'image';
  private destroy$ = new Subject<void>();
  selectedVideoId: string | null = null;

  selectedVideoShape: StudioVideoShapeDescriptor | null = null;

  @ViewChildren('textArea') textAreas: QueryList<
    ElementRef<HTMLTextAreaElement>
  >;

  get canvasTop() {
    return this.cs.canvasTop;
  }

  constructor(
    private store: Store,
    private studioService: StudioService,
    private cs: CanvasService,
    private layoutService: StudioLayoutService,
  ) {
    this.store
      .select(selectCurrentStudioScene)
      .pipe(takeUntil(this.destroy$))
      .subscribe(scene => {
        this.currentScene = cloneDeep(scene);
        // Text is now always an array
        // console.log('current-scene', this.currentScene);
        this.textValues = cloneDeep(
          (scene?.texts || []).map(
            id =>
              (this.currentScene.shapes[id] as StudioTextShapeDescriptor)?.text,
          ),
        );
        this.sceneImageIds = cloneDeep(scene?.images || []);
        this.videoInstanceIds = cloneDeep(scene?.videoInstances || []);
      });

    this.store
      .select(selectStudioImages)
      .pipe(takeUntil(this.destroy$))
      .subscribe(images => {
        this.allImages = images;
      });

    this.store
      .select(selectStudioVideos)
      .pipe(takeUntil(this.destroy$))
      .subscribe(videos => {
        this.allVideos = videos;
      });
  }

  onTextChange(event: Event, index: number) {
    const target = event.target as HTMLTextAreaElement;
    this.studioService.textChanged(target.value, index);
  }

  onTextEnter(event: Event, index: number) {
    event.preventDefault();
    const target = event.target as HTMLTextAreaElement;
    this.studioService.textChanged(target.value, index);
    target.blur();
  }

  addTextInput() {
    this.textValues.push('');
  }

  removeTextInput(index: number) {
    this.layoutService.removeText(index);
  }

  getImageThumbnailUrl(imageId: string): string {
    const image = this.allImages[imageId];
    if (!image) {
      return '';
    }
    if (image.id) {
      return `https://private.cdn.inwedio.com/images/${image.id}`;
    }
    return '';
  }

  openImageUploadModal() {
    this.mediaUploadType = 'image';
    this.showMediaUploadModal = true;
  }

  openVideoUploadModal() {
    this.mediaUploadType = 'video';
    this.showMediaUploadModal = true;
  }

  closeMediaUploadModal() {
    this.showMediaUploadModal = false;
  }

  onMediaSubmit(selectedIds: string[]) {
    this.showMediaUploadModal = false;
  }

  pendingText: boolean = false;

  @ViewChild('pendingTextArea') pendingTextRef: ElementRef<HTMLTextAreaElement>;

  ngAfterViewInit() {
    // Auto-resize all textareas on initial load
    this.textAreas.changes.subscribe(() => {
      this.resizeAllTextAreas();
    });
    // Initial resize
    setTimeout(() => this.resizeAllTextAreas());
  }

  resizeAllTextAreas() {
    this.textAreas.forEach(textArea => {
      this.resizeTextArea(textArea.nativeElement);
    });
  }

  autoResize(event: Event) {
    const textarea = event.target as HTMLTextAreaElement;
    this.resizeTextArea(textarea);
  }

  private resizeTextArea(textarea: HTMLTextAreaElement) {
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';
    // Set height to scrollHeight to fit content
    textarea.style.height = textarea.scrollHeight + 'px';
  }

  addNewText() {
    this.pendingText = true;
    setTimeout(() => {
      this.pendingTextRef.nativeElement.focus();
    });
  }

  onPendingTextChange(event: Event) {
    this.pendingText = false;
    const target = event.target as HTMLTextAreaElement;
    if (!target.value.trim()) {
      return;
    }
    this.textValues.push(target.value);
    this.layoutService.addTextShape(target.value);
  }

  onPendingTextEnter(event: Event) {
    event.preventDefault();
    const target = event.target as HTMLTextAreaElement;
    if (!target.value.trim()) {
      this.pendingText = false;
      return;
    }
    this.textValues.push(target.value);
    this.layoutService.addTextShape(target.value);
    this.pendingText = false;
  }

  removeImage(index: number) {
    this.layoutService.removeImage(index);
  }

  removeVideo(index: number) {
    // -- // -- // -- // -- // -- //
    const videoInstanceId = this.videoInstanceIds[index];
    console.log('removeVideo', videoInstanceId);
    this.layoutService.removeVideo(index);
  }

  getVideoThumbnailUrl(videoId: string): string {
    const shape = this.currentScene?.shapes[
      videoId
    ] as StudioVideoShapeDescriptor;
    if (!shape) {
      return '';
    }

    const video = this.allVideos[shape.videoId];
    if (!video) {
      return '';
    }
    return `https://private.cdn.inwedio.com/videos/${video.id}/thumbnail.jpg`;
  }

  onVideoClick(videoId: string) {
    // -- // -- // -- // -- // -- //
    this.selectedVideoId = videoId;
    this.selectedVideoShape = this.currentScene?.shapes[
      videoId
    ] as StudioVideoShapeDescriptor;
  }
  onVideoCancelled() {
    this.selectedVideoId = null;
    this.selectedVideoShape = null;
  }

  onVideoSaved(videoShape: StudioVideoShapeDescriptor) {
    this.selectedVideoId = null;
    this.selectedVideoShape = null;
    console.log('onVideoSaved', videoShape);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
